import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import ViewCalculationRule from "./ViewCalculationRule";
import "./CalculationRule.css";
import { API_BASE } from "../../../ApiUrl";
import UserTabs from "../UserRegistrations/UserTabs";

const CalculationRule = () => {
  const navigate = useNavigate();
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  // Calculation rules state
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState("");

  // Lookup tables for resolving IDs -> human-readable labels
  const [scopeCatalogs, setScopeCatalogs] = useState([]);
  const [referenceValues, setReferenceValues] = useState([]);

  // ---------------------------------------------------------------------------
  // Fetch calculation rules
  // ---------------------------------------------------------------------------
  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    setRulesError("");

    try {
      const response = await fetch(`${API_BASE}/calculation-rules/`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        setRules(result.data);
      } else {
        setRules([]);
      }
    } catch (error) {
      console.error("Error fetching calculation rules:", error);
      setRulesError("Failed to load calculation rules.");
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // ---------------------------------------------------------------------------
  // Fetch scope catalogs (for resolving `scope: [id]` -> scope name)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchScopeCatalogs = async () => {
      try {
        const response = await fetch(`${API_BASE}/scope-catalogs/`);
        if (!response.ok) return;
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setScopeCatalogs(result.data);
        }
      } catch (error) {
        console.error("Error fetching scope catalogs:", error);
      }
    };

    fetchScopeCatalogs();
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch reference values (for resolving PKs -> codes/labels)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchReferenceValues = async () => {
      try {
        const response = await fetch(`${API_BASE}/reference-values/`);
        if (!response.ok) return;
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setReferenceValues(result.data);
        }
      } catch (error) {
        console.error("Error fetching reference values:", error);
      }
    };

    fetchReferenceValues();
  }, []);

  // ---------------------------------------------------------------------------
  // Lookup helpers
  // ---------------------------------------------------------------------------
  const getReferenceById = (id) =>
    referenceValues.find((r) => r.id === id) || null;

  const getScopeById = (id) => scopeCatalogs.find((s) => s.id === id) || null;

  const resolveScopeLabel = (scopeIds) => {
    if (!Array.isArray(scopeIds) || scopeIds.length === 0) {
      return "Tenant-wide";
    }

    const labels = scopeIds
      .map((id) => {
        const scope = getScopeById(id);
        return scope ? `${scope.code} — ${scope.scope_name}` : null;
      })
      .filter(Boolean);

    return labels.length > 0 ? labels.join(", ") : "Tenant-wide";
  };

  const resolveConcludedValue = (rule) => {
    switch (rule.calculation_field_code) {
      case "QUALION_LEVEL": {
        const ref = getReferenceById(rule.concluded_qualion_level);
        return ref ? `${ref.code} — ${ref.label}` : "—";
      }
      case "DEPLOYABILITY_FLAG":
        return rule.concluded_deployability_status
          ? formatConcludedValue(rule.concluded_deployability_status)
          : "—";
      case "CANDIDATE_MENTOR_CLASSIFICATION":
        return rule.concluded_classification
          ? formatConcludedValue(rule.concluded_classification)
          : "—";
      default:
        return "—";
    }
  };

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const handleAddClick = () => {
    navigate("/admin/calculation-rules/add");
  };

  const handleEdit = (rule) => {
    navigate("/admin/calculation-rules/add", { state: { editRule: rule } });
  };

  const handleView = (rule) => {
    setSelectedRule(rule);
    setIsViewModalOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Delete rule
  // ---------------------------------------------------------------------------
  const handleDelete = async (rule) => {
    const confirmResult = await Swal.fire({
      icon: "warning",
      title: "Delete Calculation Rule?",
      html: `Are you sure you want to delete <strong>${
        rule.label || "this rule"
      }</strong>?<br/>This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      reverseButtons: true
    });

    if (!confirmResult.isConfirmed) return;

    // Show a loading indicator while the request is in flight
    Swal.fire({
      title: "Deleting...",
      text: "Please wait while the rule is being deleted.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await fetch(
        `${API_BASE}/calculation-rules/${rule.id}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      // Some backends return 204 No Content (no body) on successful delete.
      // Others return 200 with a JSON body. Handle both.
      const data =
        response.status === 204
          ? null
          : await response.json().catch(() => null);

      if (!response.ok) {
        let message = "";
        if (data?.errors && typeof data.errors === "object") {
          message = Object.entries(data.errors)
            .map(
              ([field, errs]) =>
                `${field}: ${Array.isArray(errs) ? errs.join(", ") : errs}`
            )
            .join(" | ");
        } else {
          message =
            data?.message ||
            data?.detail ||
            `Request failed with status ${response.status}`;
        }
        throw new Error(message);
      }

      // Remove from local state (optimistic UI update)
      setRules((prev) => prev.filter((r) => r.id !== rule.id));

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Calculation rule has been deleted successfully.",
        confirmButtonColor: "#3085d6",
        timer: 1800,
        timerProgressBar: true,
        showConfirmButton: false
      });

      // Optionally re-fetch from the server to stay in sync
      fetchRules();
    } catch (error) {
      console.error("Error deleting calculation rule:", error);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: error.message || "Failed to delete calculation rule.",
        confirmButtonColor: "#d33"
      });
    }
  };

  const getFieldLabel = (code) => {
    switch (code) {
      case "QUALION_LEVEL":
        return "Qualion Level";
      case "DEPLOYABILITY_FLAG":
        return "Deployability Flag";
      case "CANDIDATE_MENTOR_CLASSIFICATION":
        return "Candidate/Mentor Classification";
      default:
        return code;
    }
  };

  const formatConcludedValue = (value) => {
    if (!value) return "—";
    return String(value).replace(/_/g, " ");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="calculation-rule-page">
          <UserTabs/>
          <div className="calculation-rule-header">
            <h2>Calculation Rules</h2>
            <button className="calculation-add-btn" onClick={handleAddClick}>
              <i className="fas fa-plus"></i> Add Calculation Rule
            </button>
          </div>

          {rulesError && (
            <div
              style={{
                background: "#fdecea",
                color: "#b71c1c",
                padding: "10px 14px",
                borderRadius: "6px",
                marginBottom: "12px",
                border: "1px solid #f5c6cb"
              }}
            >
              {rulesError}
            </div>
          )}

          <div className="calculation-table-container">
            <table className="calculation-table">
              <colgroup>
                <col style={{ width: "60px" }} />
                <col style={{ width: "160px" }} />
                <col style={{ width: "220px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "90px" }} />
                <col style={{ width: "120px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Seq</th>
                  <th>Calculation Field</th>
                  <th>Scope</th>
                  <th>Rule Label</th>
                  <th>Match Type</th>
                  <th>Concluded Value</th>
                  <th>Status</th>
                  <th>Admin</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rulesLoading && (
                  <tr>
                    <td
                      colSpan="9"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Loading calculation rules...
                    </td>
                  </tr>
                )}

                {!rulesLoading && rules.length === 0 && (
                  <tr>
                    <td
                      colSpan="9"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No calculation rules found.
                    </td>
                  </tr>
                )}

                {!rulesLoading &&
                  rules.map((rule) => (
                    <tr key={rule.id}>
                      <td className="calculation-seq-cell">{rule.sequence}</td>
                      <td>
                        <span className="calculation-field-badge">
                          {getFieldLabel(rule.calculation_field_code)}
                        </span>
                      </td>
                      <td>{resolveScopeLabel(rule.scope)}</td>
                      <td className="calculation-label-cell">{rule.label}</td>
                      <td>
                        <span className="calculation-match-type">
                          {rule.match_type === "ALL_CONDITIONS"
                            ? "All conditions"
                            : "Any condition"}
                        </span>
                      </td>
                      <td>
                        <span className="calculation-concluded-value">
                          {resolveConcludedValue(rule)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`calculation-status-badge ${
                            rule.is_active
                              ? "calculation-status-active"
                              : "calculation-status-inactive"
                          }`}
                        >
                          {rule.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        {rule.requires_four_eyes_approval ? (
                          <i className="fas fa-check-circle calculation-icon-yes"></i>
                        ) : (
                          <i className="fas fa-minus-circle calculation-icon-no"></i>
                        )}
                      </td>
                      <td>
                        <div className="calculation-actions">
                          <button
                            className="calculation-action-btn calculation-view-btn"
                            title="View"
                            onClick={() => handleView(rule)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="calculation-action-btn calculation-edit-btn"
                            title="Edit"
                            onClick={() => handleEdit(rule)}
                          >
                            <i className="fas fa-pen"></i>
                          </button>
                          <button
                            className="calculation-action-btn calculation-delete-btn"
                            title="Delete"
                            onClick={() => handleDelete(rule)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {isViewModalOpen && selectedRule && (
        <ViewCalculationRule
          rule={selectedRule}
          scopeCatalogs={scopeCatalogs}
          referenceValues={referenceValues}
          onClose={() => setIsViewModalOpen(false)}
        />
      )}
    </div>
  );
};

export default CalculationRule;